import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { authFetch } from '../../utils/authFetch';
import DateInput from '../../components/DateInput';
import { applyPhoneMask } from '../../utils/masks';
import CustomAutoComplete from '../../components/CustomAutoComplete';

export default function NovaTriagem() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [formData, setFormData] = useState({
        patientName: '',
        parentsName: '',
        birthdate: '',
        age: '',
        contact: '',
        firstContactDate: '',
        screeningDate: '',
        indication: '',
        demand: '',
        demandDescription: '',
        therapistId: '',
        potentialPatient: false,
        returnContactDate: '',
        observations: '',
        becamePatient: false,
        patientId: '',
        nonPotentialPatientReason: ''
    });

    const [contactDisplay, setContactDisplay] = useState('');
    
    // Estados para autocomplete
    const [terapeutaSearch, setTerapeutaSearch] = useState('');
    const [terapeutaSelecionado, setTerapeutaSelecionado] = useState(null);
    const [pacienteSearch, setPacienteSearch] = useState('');
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);

    const indicacoes = [
        { value: 'Instagram/Site', label: 'Instagram/Site' },
        { value: 'Médico/Afins', label: 'Médico/Afins' },
        { value: 'Amigo ou Familiar', label: 'Amigo ou Familiar' },
        { value: 'Escola', label: 'Colégio' },
        { value: 'Outro', label: 'Outro' }
    ];

    const demandas = [
        { value: 'AVALIACAO', label: 'Avaliação' },
        { value: 'INTERVENCAO', label: 'Intervenção' },
        { value: 'PSICOTERAPIA', label: 'Psicoterapia' }
    ];

    // Funções de busca para autocomplete
    const buscarTerapeutas = async (termo) => {
        try {
            const response = await authFetch(`therapist?search=${encodeURIComponent(termo)}&enabled=true`);
            if (!response.ok) throw new Error('Erro ao buscar terapeutas');
            const data = await response.json();
            return data.filter(terapeuta =>
                terapeuta.name.toLowerCase().includes(termo.toLowerCase())
            );
        } catch (error) {
            console.error('Erro ao buscar terapeutas:', error);
            return [];
        }
    };

    const buscarPacientes = async (termo) => {
        try {
            const response = await authFetch(`patient?search=${encodeURIComponent(termo)}`);
            if (!response.ok) throw new Error('Erro ao buscar pacientes');
            const data = await response.json();
            return data.filter(paciente =>
                paciente.name.toLowerCase().includes(termo.toLowerCase())
            );
        } catch (error) {
            console.error('Erro ao buscar pacientes:', error);
            return [];
        }
    };

    const calcularIdade = (birthdate) => {
        if (!birthdate) {
            return;
        }

        const [year, month, day] = birthdate.split('-').map(Number);
        if (!year || !month || !day) {
            return;
        }

        const birthDate = new Date(year, month - 1, day);
        const today = new Date();

        let years = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            years--;
        }

        setFormData(prev => ({ ...prev, age: years.toString() }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'contact') {
            const masked = applyPhoneMask(value);
            setContactDisplay(masked);
            const cleanValue = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: cleanValue }));
        } else if (name === 'birthdate') {
            setFormData(prev => ({ ...prev, [name]: value }));
            // Calcular idade automaticamente se data for informada, mas permitir edição manual
            if (value) {
                calcularIdade(value);
            }
        } else if (name === 'age') {
            // Permitir apenas números
            const numericValue = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Converter idade para número
            let ageValue = null;
            if (formData.age) {
                const parsed = parseInt(formData.age, 10);
                ageValue = isNaN(parsed) ? null : parsed;
            }

            const payload = {
                patientName: formData.patientName || null,
                parentsName: formData.parentsName || null,
                firstContactDate: formData.firstContactDate || null,
                screeningDate: formData.screeningDate || null,
                birthdate: formData.birthdate || null,
                age: ageValue,
                contact: formData.contact || null,
                referral: formData.indication || null, // Mapear indication para referral
                demand: formData.demand || null,
                demandDescription: formData.demandDescription || null,
                therapistId: terapeutaSelecionado ? Number(terapeutaSelecionado.id) : null,
                potentialPatient: formData.potentialPatient || false,
                returnDate: formData.returnContactDate || null, // Mapear returnContactDate para returnDate
                observations: formData.observations || null,
                patientId: formData.becamePatient && pacienteSelecionado ? Number(pacienteSelecionado.id) : null,
                nonPotentialPatientReason: formData.becamePatient ? null : (formData.nonPotentialPatientReason || null)
            };

            const response = await authFetch('screening', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error();

            showNotification('success', 'Triagem criada com sucesso');
            navigate(`/Triagens`);
        } catch {
            showNotification('danger', 'Erro ao criar triagem');
        }
    };

    return (
        <div className="content">
            <div className="card">
                <div className="card-body p-3">
                    <form onSubmit={handleSubmit}>
                        <div className="row col-md-12 mb-3">
                            <div className="col-md-10">
                                <h2>Nova Triagem</h2>
                            </div>
                            <div className="col-md-2 text-end">
                                <Link to="/Triagens" className="btn btn-info btn-sm">Cancelar</Link>
                            </div>
                        </div>

                        <nav>
                            <div className="nav nav-tabs" role="tablist">
                                <button className="nav-link active" data-bs-toggle="tab" data-bs-target="#nav-dados" type="button">Dados</button>
                            </div>
                        </nav>

                        <div className="tab-content mt-3">
                            <div className="tab-pane fade show active" id="nav-dados">
                                <div className="row">
                                    <div className="col-md-4">
                                        <label>Nome do Paciente</label>
                                        <input 
                                            type='text' 
                                            className='form-control form-control-sm' 
                                            name="patientName" 
                                            value={formData.patientName} 
                                            onChange={handleChange} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>Nome dos Pais</label>
                                        <input 
                                            type='text' 
                                            className='form-control form-control-sm' 
                                            name="parentsName" 
                                            value={formData.parentsName} 
                                            onChange={handleChange} 
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Data de Nascimento</label>
                                        <DateInput
                                            className='form-control form-control-sm'
                                            name="birthdate"
                                            value={formData.birthdate}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Idade (anos)</label>
                                        <input 
                                            type='text' 
                                            className='form-control form-control-sm' 
                                            name="age" 
                                            value={formData.age} 
                                            onChange={handleChange}
                                            placeholder="Ex: 5"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <label>Contato (Telefone)</label>
                                        <input 
                                            type='text' 
                                            className='form-control form-control-sm' 
                                            name="contact" 
                                            value={contactDisplay} 
                                            onChange={handleChange}
                                            placeholder="(00) 00000-0000"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>Lead</label>
                                        <select 
                                            name="indication" 
                                            className='form-control form-control-sm' 
                                            value={formData.indication} 
                                            onChange={handleChange}
                                        >
                                            <option value="">Selecione</option>
                                            {indicacoes.map(ind => (
                                                <option key={ind.value} value={ind.value}>{ind.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Data Primeiro Contato</label>
                                        <DateInput
                                            className='form-control form-control-sm'
                                            name="firstContactDate"
                                            value={formData.firstContactDate}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Data Triagem</label>
                                        <DateInput
                                            className='form-control form-control-sm'
                                            name="screeningDate"
                                            value={formData.screeningDate}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-3">
                                        <label>Demanda</label>
                                        <select 
                                            name="demand" 
                                            className='form-control form-control-sm' 
                                            value={formData.demand} 
                                            onChange={handleChange}
                                        >
                                            <option value="">Selecione</option>
                                            {demandas.map(dem => (
                                                <option key={dem.value} value={dem.value}>{dem.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-12">
                                        <label>Descrição da Demanda (máx. 500 caracteres)</label>
                                        <textarea 
                                            className="form-control form-control-sm" 
                                            name="demandDescription" 
                                            value={formData.demandDescription} 
                                            onChange={handleChange}
                                            maxLength={500}
                                            rows={3}
                                        />
                                        <small className="text-muted">
                                            {formData.demandDescription.length}/500 caracteres
                                        </small>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <label>Terapeuta</label>
                                        <CustomAutoComplete
                                            placeholder="Digite o nome do terapeuta"
                                            value={terapeutaSearch}
                                            setValue={setTerapeutaSearch}
                                            onSearch={buscarTerapeutas}
                                            onSelect={(item) => {
                                                setTerapeutaSelecionado(item);
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>Potencial Paciente?</label>
                                        <select 
                                            name="potentialPatient" 
                                            className='form-control form-control-sm' 
                                            value={formData.potentialPatient ? 'true' : 'false'} 
                                            onChange={(e) => setFormData(prev => ({ ...prev, potentialPatient: e.target.value === 'true' }))}
                                        >
                                            <option value="false">Não</option>
                                            <option value="true">Sim</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label>Data Retorno do Contato</label>
                                        <DateInput
                                            className='form-control form-control-sm'
                                            name="returnContactDate"
                                            value={formData.returnContactDate}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-12">
                                        <label>Observações</label>
                                        <textarea 
                                            className="form-control form-control-sm" 
                                            name="observations" 
                                            value={formData.observations} 
                                            onChange={handleChange}
                                            rows={3}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-3">
                                        <label>Tornou-se Paciente?</label>
                                        <select 
                                            name="becamePatient" 
                                            className='form-control form-control-sm' 
                                            value={formData.becamePatient ? 'true' : 'false'} 
                                            onChange={(e) => setFormData(prev => ({ ...prev, becamePatient: e.target.value === 'true' }))}
                                        >
                                            <option value="false">Não</option>
                                            <option value="true">Sim</option>
                                        </select>
                                    </div>
                                    {formData.becamePatient && (
                                        <div className="col-md-9">
                                            <label>Selecione o Paciente</label>
                                            <CustomAutoComplete
                                                placeholder="Digite o nome do paciente"
                                                value={pacienteSearch}
                                                setValue={setPacienteSearch}
                                                onSearch={buscarPacientes}
                                                onSelect={(item) => {
                                                    setPacienteSelecionado(item);
                                                }}
                                            />
                                        </div>
                                    )}
                                    {!formData.becamePatient && (
                                        <div className="col-md-9">
                                            <label>Justificativa</label>
                                            <textarea
                                                name="nonPotentialPatientReason"
                                                className="form-control form-control-sm"
                                                placeholder="Informe o motivo (máx. 500 caracteres)"
                                                value={formData.nonPotentialPatientReason}
                                                onChange={(e) => setFormData(prev => ({ ...prev, nonPotentialPatientReason: e.target.value.slice(0, 500) }))}
                                                maxLength={500}
                                                rows={3}
                                            />
                                            <small className="text-muted">{formData.nonPotentialPatientReason.length}/500</small>
                                        </div>
                                    )}
                                </div>

                                <div className="card-footer" style={{ paddingLeft: 0, marginTop: '20px' }}>
                                    <button type="submit" className="btn btn-primary btn-sm">Salvar</button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

